import axios from "axios";
import { nuclearn_api_url } from "src/App";
import { useQuery, useQueryClient } from "react-query";
import type { SDRProcessingStatusResponse } from "src/types/cloudFiles";
import type { SDRStatusEvent } from "src/types/notificationEvents";
import {
  useNotificationStream,
  isNotificationStreamHealthy,
  useSSEFallbackInvalidation,
} from "./useNotificationStream";

// Re-export for backward compatibility
export type ProcessingStatus = SDRProcessingStatusResponse;

interface UseProcessingStatusOptions {
  datasetId?: number;
  sdrId?: number; // Source Data Record ID for Option A architecture
  filename?: string; // To match specific file when querying by datasetId
  enabled?: boolean;
  refetchInterval?: number | false;
}

/**
 * Hook to fetch processing status for a dataset or specific SDR from the backend API.
 * Uses React Query for initial fetch + SSE for real-time updates.
 * Falls back to polling if the SSE connection is unhealthy.
 */
export const useProcessingStatus = ({
  datasetId,
  sdrId,
  filename,
  enabled = true,
}: UseProcessingStatusOptions) => {
  const queryClient = useQueryClient();
  const queryKey = ["processing-status", datasetId, sdrId, filename];

  const query = useQuery<ProcessingStatus | null>(
    queryKey,
    async () => {
      if (!datasetId && !sdrId) return null;

      // Prefer sdrId if provided (Option A), fallback to datasetId (Option B)
      const queryParam = sdrId
        ? `sdr_ids=${sdrId}`
        : `dataset_ids=${datasetId}`;

      const response = await axios.get<ProcessingStatus[]>(
        `${nuclearn_api_url}/datasets/processing-status?${queryParam}`,
      );

      // A dataset query returns other files' rows too, so only a filename
      // match is this file; no match means "not found yet", never another row.
      let statusData: ProcessingStatus | null;
      if (sdrId) {
        statusData =
          response.data.find((item) => item.source_data_record_id === sdrId) ||
          null;
      } else if (filename) {
        statusData =
          response.data.find((item) => item.filename === filename) || null;

        // A row stored without the extension; stripping the row's own
        // extension too would match a different file such as report.docx.
        if (!statusData) {
          const filenameWithoutExt = filename.replace(/\.[^/.]+$/, "");
          statusData =
            response.data.find(
              (item) => item.filename === filenameWithoutExt,
            ) || null;
        }

        // If still no match, try case-insensitive match
        if (!statusData) {
          const filenameLower = filename.toLowerCase();
          statusData =
            response.data.find(
              (item) => item.filename.toLowerCase() === filenameLower,
            ) || null;
        }
      } else {
        statusData = null;
      }

      return statusData;
    },
    {
      enabled: enabled && !!(datasetId || sdrId),
      // SSE provides real-time updates; only poll as fallback when SSE is down
      refetchInterval: (data) => {
        // If SSE is healthy, no need to poll
        if (isNotificationStreamHealthy() && sdrId) {
          return false;
        }
        // Fallback: poll while processing
        if (!data || data.is_processing) {
          return 2000;
        }
        return false;
      },
      keepPreviousData: false,
      staleTime: 0,
      refetchIntervalInBackground: false,
    },
  );

  // Subscribe to SSE updates for this SDR
  const streamKey = sdrId ? `notify:sdr:${sdrId}:status` : null;

  useNotificationStream(streamKey, "sdr_status", (raw) => {
    const data = raw as unknown as SDRStatusEvent;
    // Explicit undefined checks (not ??) so backend nulls like
    // error_message: null (clearing an error) still apply.
    queryClient.setQueryData(
      queryKey,
      (prev: ProcessingStatus | null | undefined) => {
        if (!prev) {
          // Cache empty — the initial REST fetch hasn't returned yet.
          // Dropping the event here loses status updates that arrived
          // during the fetch race, leaving the UI stuck on stale state
          // until the next reconnect triggers fallback refetch (~85s).
          // Invalidate to force a refetch with current state; React Query
          // dedupes concurrent invalidations of the same key.
          queryClient.invalidateQueries(queryKey);
          return prev ?? null;
        }
        // Same-reference return skips re-renders of every subscriber when
        // an SSE event carries values already in the cache — common under
        // load at 5k users.
        if (
          (data.status === undefined || data.status === prev.status) &&
          (data.display_status === undefined ||
            data.display_status === prev.display_status) &&
          (data.is_processing === undefined ||
            data.is_processing === prev.is_processing) &&
          (data.progress_percentage === undefined ||
            data.progress_percentage === prev.progress_percentage) &&
          (data.error_message === undefined ||
            data.error_message === prev.error_message)
        ) {
          return prev;
        }
        return {
          ...prev,
          status: data.status !== undefined ? data.status : prev.status,
          // display_status and progress_percentage are non-null in the
          // cache shape — treat a null event value as "leave unchanged"
          // rather than writing an invalid null into the cache.
          display_status: data.display_status ?? prev.display_status,
          is_processing:
            data.is_processing !== undefined
              ? data.is_processing
              : prev.is_processing,
          progress_percentage:
            data.progress_percentage ?? prev.progress_percentage,
          error_message:
            data.error_message !== undefined
              ? data.error_message
              : prev.error_message,
        };
      },
    );
  });

  useSSEFallbackInvalidation(queryClient, queryKey, !!sdrId);

  return query;
};
